/**
 * Impersonation banner.
 *
 * Renders a fixed gold strip at the very top of the app whenever the
 * current session was minted via the admin "Impersonate demo account"
 * flow. `/api/auth/me` returns `impersonated_by` (the admin's name) in
 * that case, which we use to drive this banner.
 *
 * The single CTA — "Return to admin" — calls /api/auth/end-impersonation;
 * the backend swaps the cookie back to a fresh admin session and we hard
 * reload so AuthContext picks up the original user.
 */
import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { hasTabSession, clearTabToken } from '../lib/tabSession';
import { UserCog, LogOut } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ImpersonationBanner() {
  const { user } = useAuth();
  const [returning, setReturning] = useState(false);
  const tabScoped = hasTabSession();

  // Iter-158 removed the banner from same-window impersonation on PM
  // dashboards. It is reinstated ONLY for the new per-tab demo windows,
  // where it is the affordance to identify and close a comparison window.
  if (!tabScoped) return null;
  if (!user || !user.impersonated_by) return null;

  const returnToAdmin = async () => {
    setReturning(true);
    // A side-by-side demo window lives only in this tab. Closing it must not
    // disturb the admin's own window — just drop the tab token and close.
    if (tabScoped) {
      clearTabToken();
      window.close();
      toast.message('Close this window to return to your admin session.');
      setReturning(false);
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/end-impersonation`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Could not return to admin session');
        setReturning(false);
        return;
      }
      toast.success('Returning to admin…');
      window.location.href = '/';
    } catch (err) {
      toast.error('Network error');
      setReturning(false);
    }
  };

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[60] flex items-center justify-center gap-4 px-4 py-2 text-black text-sm shadow-md"
      style={{
        backgroundColor: '#C9A962',
        borderBottom: '2px solid #2D2D2D',
        fontFamily: 'Barlow Condensed, sans-serif',
        letterSpacing: '0.04em',
      }}
      data-testid="impersonation-banner"
      role="alert"
    >
      <UserCog className="h-4 w-4" />
      <span className="font-bold uppercase">
        Impersonating <span className="underline">{user.name}</span>
      </span>
      <span className="hidden sm:inline opacity-75">
        · originally signed in as {user.impersonated_by}
      </span>
      <button
        type="button"
        onClick={returnToAdmin}
        disabled={returning}
        className="ml-2 inline-flex items-center gap-1 rounded-full px-3 py-0.5 text-xs font-bold uppercase transition"
        style={{ backgroundColor: '#2D2D2D', color: '#C9A962' }}
        data-testid="impersonation-return-btn"
      >
        <LogOut className="h-3 w-3" />
        {returning ? (tabScoped ? 'Closing…' : 'Returning…') : (tabScoped ? 'Close demo window' : 'Return to admin')}
      </button>
    </div>
  );
}
